import { motion } from 'framer-motion'

import { styled } from 'styled-system/jsx'

const CompareBar = styled('div', {
  base: {
    position: 'fixed',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#FFFFFF',
    borderRadius: 'pill',
    boxShadow: '0 24px 48px -16px rgba(26, 19, 48, 0.32)',
    border: '1.5px solid',
    borderColor: 'quickpass.primarySoft',
    paddingX: '20px',
    paddingY: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    zIndex: '85',
  },
})

const CompareBarText = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
    color: 'quickpass.ink',
  },
})

const CompareBarPrimary = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '700',
    color: '#FFFFFF',
    backgroundColor: 'quickpass.primary',
    border: 'none',
    borderRadius: 'pill',
    paddingX: '14px',
    paddingY: '7px',
    cursor: 'pointer',
    transition: 'transform {durations.fast} {easings.appleStandard}',
    _hover: { transform: 'translateY(-1px)' },
    _disabled: { opacity: '0.5', cursor: 'not-allowed' },
  },
})

const CompareBarSecondary = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
    color: 'quickpass.inkSoft',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '0',
    transition: 'color {durations.fast} {easings.appleStandard}',
    _hover: { color: 'quickpass.warmth' },
  },
})

const PageStack = styled(motion.div, {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
})

const Header = styled(motion.div, {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    paddingX: '4px',
    paddingY: '8px',
  },
})

const HeaderRow = styled('div', {
  base: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: '12px',
  },
})

const HeaderText = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
})

const PageTitle = styled('h1', {
  base: {
    fontFamily: 'display',
    fontSize: '32px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '0',
    letterSpacing: '-0.5px',
  },
})

const PageSubtitle = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '16px',
    color: 'quickpass.inkSoft',
    margin: '0',
    maxWidth: '560px',
  },
})

const ScopeToggle = styled('div', {
  base: {
    display: 'inline-flex',
    gap: '4px',
    padding: '4px',
    backgroundColor: '#F3EFFF',
    borderRadius: 'pill',
    alignSelf: 'flex-start',
    marginLeft: '4px',
  },
})

const ScopeButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
    border: 'none',
    borderRadius: 'pill',
    paddingX: '14px',
    paddingY: '7px',
    cursor: 'pointer',
    transition:
      'color {durations.fast} {easings.appleStandard}, background-color {durations.fast} {easings.appleStandard}, box-shadow {durations.fast} {easings.appleStandard}',
  },
  variants: {
    isActive: {
      true: {
        backgroundColor: '#FFFFFF',
        color: 'quickpass.ink',
        boxShadow: '0 4px 12px -6px rgba(26, 19, 48, 0.25)',
      },
      false: {
        backgroundColor: 'transparent',
        color: 'quickpass.inkSoft',
        _hover: { color: 'quickpass.ink' },
      },
    },
  },
})

const SectionTitle = styled('h2', {
  base: {
    fontFamily: 'display',
    fontSize: '20px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '0 0 12px 4px',
    letterSpacing: '-0.3px',
  },
})

const ListingsGrid = styled('div', {
  base: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '16px',
  },
})

const ListingTile = styled('div', {
  base: {
    backgroundColor: '#FFFFFF',
    borderRadius: 'lg',
    padding: '20px',
    boxShadow: '0 12px 32px -16px rgba(128, 84, 255, 0.15)',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    border: '1.5px solid transparent',
    transition:
      'transform {durations.fast} {easings.appleStandard}, border-color {durations.fast} {easings.appleStandard}',
    _hover: {
      borderColor: 'quickpass.primary',
      transform: 'translateY(-2px)',
    },
  },
})

const ListingId = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    color: 'quickpass.inkSoft',
    fontWeight: '600',
    letterSpacing: '0.5px',
  },
})

const ListingTitle = styled('span', {
  base: {
    fontFamily: 'display',
    fontSize: '17px',
    fontWeight: '700',
    color: 'quickpass.ink',
  },
})

const ListingActions = styled('div', {
  base: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '4px',
  },
})

const RemoveButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
    color: 'quickpass.inkSoft',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '0',
    transition: 'color {durations.fast} {easings.appleStandard}',
    _hover: {
      color: 'quickpass.warmth',
    },
  },
})

const EmptyCard = styled('div', {
  base: {
    backgroundColor: '#FFFFFF',
    borderRadius: 'lg',
    padding: '40px 32px',
    textAlign: 'center',
    border: '1.5px dashed',
    borderColor: '#E5E0F5',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },
})

const EmptyEmoji = styled('span', {
  base: {
    fontSize: '48px',
    lineHeight: '1',
  },
})

const EmptyTitle = styled('p', {
  base: {
    fontFamily: 'display',
    fontSize: '20px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '0',
  },
})

const EmptyBody = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '15px',
    color: 'quickpass.inkSoft',
    margin: '0',
    maxWidth: '460px',
    lineHeight: '1.5',
  },
})

const EmptyActions = styled('div', {
  base: {
    marginTop: '8px',
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
  },
})

export {
  PageStack,
  Header,
  HeaderRow,
  HeaderText,
  PageTitle,
  PageSubtitle,
  ScopeToggle,
  ScopeButton,
  SectionTitle,
  ListingsGrid,
  ListingTile,
  ListingId,
  ListingTitle,
  ListingActions,
  RemoveButton,
  EmptyCard,
  EmptyEmoji,
  EmptyTitle,
  EmptyBody,
  EmptyActions,
  CompareBar,
  CompareBarText,
  CompareBarPrimary,
  CompareBarSecondary,
}
